import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Briefcase, Building2, Users, ArrowRight, GraduationCap, Banknote, Brain } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-paper">
      {/* Top stripe */}
      <div className="h-1.5 gradient-stripe" />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-heading font-bold">PS</div>
            <div>
              <div className="font-heading font-bold text-xl tracking-tight text-[#1F2937]">padaskillinfo</div>
              <div className="text-xs text-gray-500 -mt-0.5">Pithapuram Skilling Census · RTIH</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="#about" className="text-gray-700 hover:text-[#1E3A8A]">About</a>
            <Link to="/courses" className="text-gray-700 hover:text-[#1E3A8A]" data-testid="header-courses-link">Courses</Link>
            <a href="#how" className="text-gray-700 hover:text-[#1E3A8A]">How it works</a>
            <Link to="/stats" className="text-gray-700 hover:text-[#1E3A8A]" data-testid="header-stats-link">Live Stats</Link>
            <Link to="/login" className="text-gray-700 hover:text-[#1E3A8A]" data-testid="header-login-link">Officer Login</Link>
            <Link to="/register" data-testid="header-register-link">
              <Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white rounded-full px-5">
                Register Now <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 bg-grid-soft opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-fade-up">
            <div
              className="radium-pill inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full mb-6"
              data-testid="hero-radium-badge"
            >
              <Sparkles className="w-4 h-4 text-[#39FF14] drop-shadow-[0_0_4px_#39FF14]" />
              <span className="radium-text font-heading tracking-[0.15em] uppercase text-[13px] sm:text-sm font-extrabold">
                Pithapuram Constituency · Day-to-day live data
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shadow-[0_0_8px_#39FF14] animate-pulse" />
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1F2937] leading-[1.05]">
              Skilling census for a <span className="text-[#1E3A8A]">stronger</span>,
              <br /> <span className="text-[#E11D48]">self-reliant</span> rural youth.
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-xl">
              Empowering unemployed and dropped-out youth across Pithapuram villages with AI upskilling,
              startup support, bank linkages and placements through the RTIH Incubation Centre.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register-btn">
                <Button className="h-12 px-7 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full text-base">
                  Register your details <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-login-btn">
                <Button variant="outline" className="h-12 px-7 rounded-full text-base border-gray-300">
                  Officer Dashboard
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <Stat color="bg-[#1E3A8A]" icon={<Users className="w-4 h-4 text-white" />} label="Villages" value="50+" />
              <Stat color="bg-[#F59E0B]" icon={<Brain className="w-4 h-4 text-white" />} label="AI Tracks" value="All Domains" />
              <Stat color="bg-[#10B981]" icon={<Banknote className="w-4 h-4 text-white" />} label="Bank linkages" value="∞" />
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src="https://images.unsplash.com/photo-1609252509229-364936a1d1a2?crop=entropy&cs=srgb&fm=jpg&w=940&q=85"
                alt="Rural Youth"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-90">RTIH Incubation</div>
                <div className="font-heading font-bold text-2xl mt-1">From village to startup.</div>
              </div>
            </div>
            {/* Floating cards */}
            <div className="absolute -left-6 top-10 bg-white rounded-xl shadow-xl px-4 py-3 border border-gray-100 hidden md:flex items-center gap-3 animate-pulse-ring">
              <div className="w-9 h-9 rounded-lg bg-[#F59E0B] flex items-center justify-center"><Brain className="w-4 h-4 text-white" /></div>
              <div>
                <div className="text-xs text-gray-500">AI Upskill</div>
                <div className="font-heading font-bold text-sm">Free training</div>
              </div>
            </div>
            <div className="absolute -right-4 bottom-12 bg-white rounded-xl shadow-xl px-4 py-3 border border-gray-100 hidden md:flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#10B981] flex items-center justify-center"><Banknote className="w-4 h-4 text-white" /></div>
              <div>
                <div className="text-xs text-gray-500">Bank Loan</div>
                <div className="font-heading font-bold text-sm">Linkage support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="about" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="text-sm font-semibold text-[#E11D48] uppercase tracking-wider">What we offer</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Four pillars of empowerment</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard color="#1E3A8A" icon={<Brain />} title="AI Upskill in any domain" desc="Every NSDC Sector Skill Council course offered with AI integration — from agriculture to telecom — tailored for rural youth." />
          <FeatureCard color="#F59E0B" icon={<Briefcase />} title="Placements" desc="Link with private, government and remote job opportunities." />
          <FeatureCard color="#E11D48" icon={<Building2 />} title="Startup Support" desc="RTIH Incubation: mentoring, co-working, business development." />
          <FeatureCard color="#10B981" icon={<Banknote />} title="Bank Linkages" desc="MUDRA, PMEGP, NRLM and SHG-linked credit guidance." />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-8">
            <Step n="01" title="Register" desc="Fill the multi-step census form: identity, education, interests, employment & startup goals." color="#1E3A8A" />
            <Step n="02" title="Get matched" desc="Our team maps you to AI tracks, employers, and incubation programs at RTIH Pithapuram." color="#F59E0B" />
            <Step n="03" title="Grow" desc="Receive training, placements or bank-linked startup capital. Track progress on the dashboard." color="#10B981" />
          </div>
        </div>
      </section>

      <footer className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white text-[#1E3A8A] flex items-center justify-center font-heading font-bold">PS</div>
            <div>
              <div className="font-heading font-bold">padaskillinfo</div>
              <div className="text-xs text-white/70">RTIH Incubation Centre · Pithapuram</div>
            </div>
          </div>
          <div className="text-sm text-white/80">© {new Date().getFullYear()} Pithapuram Skilling Census · For rural youth empowerment</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ color, icon, label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
      <div className={`w-7 h-7 ${color} rounded-md flex items-center justify-center mb-2`}>{icon}</div>
      <div className="font-heading font-bold text-xl">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function FeatureCard({ color, icon, title, desc }) {
  return (
    <div className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="font-heading font-bold text-lg mb-1">{title}</div>
      <div className="text-sm text-gray-600">{desc}</div>
    </div>
  );
}

function Step({ n, title, desc, color }) {
  return (
    <div className="flex gap-5">
      <div className="font-heading font-bold text-5xl" style={{ color }}>{n}</div>
      <div>
        <div className="font-heading font-bold text-xl">{title}</div>
        <div className="text-gray-600 mt-1">{desc}</div>
      </div>
    </div>
  );
}
